import React, { useState, useCallback, useEffect } from "react";
import { SearchBar } from "components/Launchables/search-bar";
import { MarketplaceLaunchableCardWrapper } from "components/Launchables/marketplace-launchable-card";
import { Spinner } from "components/UI-lib";
import LaunchableInteractive from "components/Launchables/List/LaunchableInteractive";
import { useQueryClient } from "@tanstack/react-query";
import type { SearchLaunchablesResponse } from "server/devplane/launchables/api";
import { launchableSearchQueryOptions } from "server/devplane/launchables/queries";

interface BrowseLaunchablesProps {
  selectedLaunchableId: string | null;
  onSelect: (launchable: any) => void;
  defaultGPU?: string;
  defaultCloudProvider?: string;
}

export const BrowseLaunchables: React.FC<BrowseLaunchablesProps> = ({
  selectedLaunchableId,
  onSelect,
  defaultGPU = "Any",
  defaultCloudProvider = "Any",
}) => {
  const queryClient = useQueryClient();
  const [searchResults, setSearchResults] =
    useState<SearchLaunchablesResponse | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedGPU, setSelectedGPU] = useState<string>(defaultGPU);
  const [selectedCloudProvider, setSelectedCloudProvider] =
    useState<string>(defaultCloudProvider);
  const [currentCursor, setCurrentCursor] = useState<string>("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = React.useRef<(() => void) | null>(null);

  const fetchInitial = useCallback(async () => {
    const filters: Record<string, string[]> = {};
    if (selectedGPU && selectedGPU !== "Any") {
      filters.gpu_name = [selectedGPU.trim()];
    }
    if (selectedCloudProvider && selectedCloudProvider !== "Any") {
      filters.provider = [selectedCloudProvider.toLowerCase().trim()];
    }

    try {
      const results = await queryClient.fetchQuery(
        launchableSearchQueryOptions({
          query: "",
          filters,
          pageSize: 21,
        }),
      );
      setSearchResults(results);
    } catch {
      setSearchResults(null);
    }
  }, [queryClient, selectedCloudProvider, selectedGPU]);

  const handleSearch = useCallback(
    (results: SearchLaunchablesResponse | null, append: boolean = false) => {
      if (results === null) {
        // Don't clear results on null if we want to show initial data,
        // but SearchBar sends null when cleared.
        // We might want to re-fetch initial data here or just clear.
        // For now let's re-fetch initial if null.
        void fetchInitial();
      } else if (append && searchResults) {
        setSearchResults({
          ...results,
          results: [
            ...(searchResults.results || []),
            ...(results.results || []),
          ],
          totalResultsCount: results.totalResultsCount,
          pageResultsCount:
            searchResults.pageResultsCount + results.pageResultsCount,
        });
        setCurrentCursor(results.cursor);
      } else {
        setSearchResults(results);
        setCurrentCursor(results.cursor);
      }
    },
    [fetchInitial, searchResults],
  );

  useEffect(() => {
    if (defaultGPU && defaultGPU !== "Any") {
      setSelectedGPU(defaultGPU);
    }
    if (defaultCloudProvider && defaultCloudProvider !== "Any") {
      setSelectedCloudProvider(defaultCloudProvider);
    }
  }, [defaultGPU, defaultCloudProvider]);

  useEffect(() => {
    void fetchInitial();
  }, [fetchInitial]);

  return (
    <div className="w-full">
      <div className="mb-6">
        <SearchBar
          onSearch={handleSearch}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          selectedGPU={selectedGPU}
          setSelectedGPU={setSelectedGPU}
          selectedCloudProvider={selectedCloudProvider}
          setSelectedCloudProvider={setSelectedCloudProvider}
          currentCursor={currentCursor}
          onLoadMoreRef={loadMoreRef}
          isLoadingMore={isLoadingMore}
          setIsLoadingMore={setIsLoadingMore}
        />
      </div>

      {searchResults && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-2">
            {(searchResults.results || []).map((result) => (
              <div
                key={result.environment_id || (result as any).id}
                className={`
                    rounded-lg transition-all relative
                    ${
                      selectedLaunchableId ===
                      (result.environment_id || (result as any).id)
                        ? "ring-2 ring-highlight z-10"
                        : "z-0"
                    }
                `}
              >
                <MarketplaceLaunchableCardWrapper
                  environment={result}
                  disableScale={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(result);
                  }}
                />
              </div>
            ))}
          </div>

          {isLoadingMore && (
            <div className="flex justify-center py-8">
              <Spinner type="primary" />
            </div>
          )}

          {(searchResults.results || []).length === 0 && (
            <div className="text-center py-12">
              <div>
                <LaunchableInteractive
                  isSectionInfoEnabled={false}
                  isAnimationEnabled={true}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-primary">
                No Launchables found
              </h3>
              <p className="text-secondary mb-4">
                {defaultGPU && defaultGPU !== "Any"
                  ? "There are no launchables built specifically for this instance type"
                  : "Try adjusting your search or filters."}
              </p>
            </div>
          )}
        </>
      )}

      {!searchResults && (
        <div className="flex justify-center py-12">
          <Spinner type="primary" />
        </div>
      )}
    </div>
  );
};
